// src/pages/GroupBusinesses.jsx
import React, { useMemo, useState, useCallback, useEffect } from "react";
import {
  Box, Typography, Grid, Card, CardContent, Button, Chip, Divider, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Stack, InputAdornment,
  Snackbar, Alert,
} from "@mui/material";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import StorefrontIcon from "@mui/icons-material/Storefront";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { motion } from "framer-motion";
import Swal from "sweetalert2";
import { useAuth } from "../contexts/AuthContext";
import LoadingSpinner from "../components/LoadingSpinner";
import dayjs from "dayjs";
import formatDate from "../shared/formatDate";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
// import { formatPhoneNumber } from "../shared/formatter";
import PhoneInput from "../components/PhoneInput";
import { fetchBusinessInvoices, fetchGroupBusinesses, createGroupBusiness, approveGroupBusiness, createBusinessInvoice, approveBusinessInvoice, downloadBusinessInvoice } from "../services/groupBusinessService";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useTranslation } from "react-i18next";

const brand = "#800b33";
const brandHover = "#6c0a2b";

const currency = (n = 0) => new Intl.NumberFormat("en-TZ").format(Number(n) || 0);
const createInvoiceReference = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  const rand = Math.floor(Math.random() * 1e9);
  return `INV-${Date.now()}-${rand}`;
};

export const getBrowserLocalInvoiceDate = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const formatInvoiceDateForDisplay = (dateValue) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateValue || "").slice(0, 10));
  if (!match) return "";
  return `${match[3]}/${match[2]}/${match[1]}`;
};

export const createEmptyInvoiceLineItem = () => ({
  description: "",
  quantity: 1,
  unit_price: "",
});

export const createBusinessInvoiceDraft = (referenceFactory = createInvoiceReference, date = new Date()) => ({
  date: getBrowserLocalInvoiceDate(date),
  description: "",
  customer_name: "",
  customer_phone: "",
  customer_address: "",
  payment_reference: referenceFactory(),
});

// ---- Approval constants
const BUSINESS_STATUS = {
  PENDING_APPROVAL: "PENDING_APPROVAL",
  ACTIVE: "ACTIVE",
  REJECTED: "REJECTED",
};

const INVOICE_STATUS = { // These should ideally come from a shared constant or backend
  DRAFT: "DRAFT",
  PENDING_SECRETARY_APPROVAL: "PENDING_SECRETARY_APPROVAL",
  PENDING_ACCOUNTANT_APPROVAL: "PENDING_ACCOUNTANT_APPROVAL",
  APPROVED: "APPROVED", // approved by chain, waiting customer bank credit capture
};

// ======= Small reusable bits =======
const StatCard = ({ icon, label, value, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.06, duration: 0.35 }}
  >
    <Card
      sx={{
        p: 2,
        borderRadius: 3,
        boxShadow: 3,
        "&:hover": { boxShadow: 6, transform: "translateY(-3px)" },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box
          sx={{
            width: 52, height: 52, borderRadius: "50%",
            background: `${brand}1A`, display: "grid", placeItems: "center",
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
          <Typography variant="h6" fontWeight={800}>{value}</Typography>
        </Box>
      </Stack>
    </Card>
  </motion.div>
);

const StatusChip = ({ status }) => {
  const { t } = useTranslation();
  if (status === BUSINESS_STATUS.PENDING_APPROVAL) {
    return <Chip label={t("Pending Admin")} color="warning" size="small" />;
  }
  if (status === BUSINESS_STATUS.ACTIVE) {
    return <Chip label={t("Active")} color="success" size="small" />;
  }
  if (status === BUSINESS_STATUS.REJECTED) {
    return <Chip label={t("Rejected")} color="error" size="small" />;
  }
  return <Chip label={t(status)} size="small" />;
};

const InvoiceStatusChip = ({ status }) => {
  const { t } = useTranslation();
  const map = {
    [INVOICE_STATUS.DRAFT]: { label: t("Draft"), color: "default" },
    [INVOICE_STATUS.PENDING_SECRETARY_APPROVAL]: { label: t("Pending Katibu"), color: "warning" },
    [INVOICE_STATUS.PENDING_ACCOUNTANT_APPROVAL]: { label: t("Pending Muhasibu"), color: "warning" },
    [INVOICE_STATUS.APPROVED]: { label: t("Approved (bank capture pending)"), color: "info" },
  };
  const meta = map[status] || { label: t(status), color: "default" };
  return <Chip label={meta.label} color={meta.color} size="small" />;
};

const normalizePaymentStatus = (rawStatus = "") => {
  const status = String(rawStatus || "").trim().toUpperCase();
  if (!status) return "";
  if (["PAID", "SETTLED", "COMPLETED"].includes(status)) return "PAID";
  if (["PARTIAL", "PARTIALLY_PAID", "PART_PAYMENT"].includes(status)) return "PARTIAL";
  if (["OVERDUE", "LATE"].includes(status)) return "OVERDUE";
  if (["UNPAID", "PENDING", "AWAITING_PAYMENT"].includes(status)) return "PENDING";
  return status;
};

const PAYMENT_STATUS_META = {
  NOT_READY: { labelKey: "Awaiting approval", color: "default" },
  PENDING: { labelKey: "Awaiting payment", color: "default" },
  PARTIAL: { labelKey: "Partially paid", color: "warning" },
  OVERDUE: { labelKey: "Overdue", color: "error" },
  PAID: { labelKey: "Paid in full", color: "success" },
};

const getInvoicePaymentMeta = (invoice = {}, t) => {
  const payable = Number(invoice.total_amount ?? invoice.amount ?? invoice.base_amount ?? 0);
  const paid = Number(invoice.amount_paid ?? invoice.paid_amount ?? invoice.amount_received ?? 0);
  const outstanding = Math.max(payable - paid, 0);
  const backendStatus = normalizePaymentStatus(
    invoice.payment_status || invoice.collection_status || invoice.disbursement_status || ""
  );
  const isApproved = invoice.status === INVOICE_STATUS.APPROVED;
  let statusKey = backendStatus;

  if (!backendStatus && isApproved) {
    if (paid >= payable && payable > 0) statusKey = "PAID";
    else if (paid > 0) statusKey = "PARTIAL";
    else statusKey = "PENDING";
  }

  if (!isApproved) {
    statusKey = "NOT_READY";
  }

  const meta = PAYMENT_STATUS_META[statusKey] || { labelKey: statusKey || "Awaiting payment data", color: "default" };
  let label = meta.labelKey ? t(meta.labelKey) : t(statusKey || "Awaiting payment data");
  if (isApproved && ["PENDING", "PARTIAL", "OVERDUE"].includes(statusKey) && outstanding > 0) {
    label = t("Payment due label", { label, amount: currency(outstanding) });
  }

  return {
    payable,
    paid,
    outstanding,
    showPayment: isApproved,
    statusLabel: label,
    statusColor: meta.color,
  };
};

const formatLineItemsTooltip = (t, items = []) => {
  if (!items || !items.length) {
    return t("No line items captured.");
  }
  return (
    <Box sx={{ py: 0.5 }}>
      {items.map((item, index) => {
        const desc = (item.description || t("Item")).trim() || t("Item");
        const qty = item.quantity || 0;
        const unit = currency(item.unit_price || 0);
        const total = currency((item.quantity || 0) * (item.unit_price || 0));
        return (
          <Typography variant="caption" display="block" key={`line-item-tip-${index}`}>
            {t("Line item tooltip", {
              index: index + 1,
              description: desc,
              quantity: qty,
              unit,
              total,
            })}
          </Typography>
        );
      })}
    </Box>
  );
};

// ======= Approval stepper (visual only) =======
const ApprovalChain = ({ status }) => {
  const { t } = useTranslation();
  const steps = [
    { key: INVOICE_STATUS.PENDING_SECRETARY_APPROVAL, label: t("Chair ✓ → Katibu") },
    { key: INVOICE_STATUS.PENDING_ACCOUNTANT_APPROVAL, label: t("Katibu ✓ → Muhasibu") },
    { key: INVOICE_STATUS.APPROVED, label: t("Muhasibu ✓ (Approved, await bank credit)") },
  ];

  const activeIndex = (() => {
    switch (status) {
      case INVOICE_STATUS.PENDING_SECRETARY_APPROVAL: return 0;
      case INVOICE_STATUS.PENDING_ACCOUNTANT_APPROVAL: return 1;
      case INVOICE_STATUS.APPROVED: return 2; // Final step
      default: return -1; // draft
    }
  })();

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {steps.map((s, i) => (
        <React.Fragment key={s.key}>
          <Chip
            size="small"
            variant={i <= activeIndex ? "filled" : "outlined"}
            color={i <= activeIndex ? "success" : "default"}
            label={s.label}
          />
          {i < steps.length - 1 && <ArrowForwardIcon fontSize="small" sx={{ opacity: 0.4 }} />}
        </React.Fragment>
      ))}
    </Stack>
  );
};

// ======= Dialogs =======
const RegisterBusinessDialog = ({ open, onClose, onSave }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "",
    type: "Event Service",
    capital: "",
    description: "",
  });

  const handleSave = () => {
    if (!form.name || !form.type || !form.capital) {
      Swal.fire({
        icon: "warning",
        title: t("Missing fields"),
        text: t("Name, Type, and Capital are required."),
        confirmButtonColor: brand,
      });
      return;
    }
    onSave({ ...form, capital: Number(form.capital) });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {t("Register Group Business")}
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField label={t("Business Name")} fullWidth value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t("Business Type")}</InputLabel>
              <Select label={t("Business Type")} value={form.business_type}
                onChange={(e) => setForm((f) => ({ ...f, business_type: e.target.value }))}>
                <MenuItem value="RENTAL">{t("Rental")}</MenuItem>
                <MenuItem value="SERVICE">{t("Service")}</MenuItem>
                <MenuItem value="PRODUCT_SALES">{t("Product Sales")}</MenuItem>
                <MenuItem value="OTHER">{t("Other")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label={t("Starting Capital (TZS)")} fullWidth
              value={form.capital ? Number(form.capital).toLocaleString("en-US") : ""}
              onChange={(e) => {
                const val = e.target.value.replace(/,/g, "");
                if (!isNaN(val)) setForm((f) => ({ ...f, capital: val }));
              }} />
          </Grid>
          <Grid item xs={12}>
            <TextField label={t("Description")} fullWidth multiline minRows={3} value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("Cancel")}</Button>
        <Button variant="contained" sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }} onClick={handleSave}>
          {t("Submit (Admin Approval)")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const CreateInvoiceDialog = ({ open, onClose, onSave, business }) => {
  const { t } = useTranslation();

  const [form, setForm] = useState(() => createBusinessInvoiceDraft());
  const [fieldErrors, setFieldErrors] = useState({});
  const [lineItems, setLineItems] = useState([createEmptyInvoiceLineItem()]);
  const [lineItemErrors, setLineItemErrors] = useState([{}]);

  useEffect(() => {
    setFieldErrors({});
    setLineItemErrors([{}]);
    if (open) {
      setForm(createBusinessInvoiceDraft());
      setLineItems([createEmptyInvoiceLineItem()]);
    }
  }, [open, business?.id]);

  const operationFeePercent = Number(business?.operation_fee_percent || 0);
  const computeLineTotal = (item = {}) => {
    const qty = Number(item.quantity) || 0;
    const price = Number(item.unit_price) || 0;
    return qty * price;
  };
  const baseAmount = lineItems.reduce((sum, item) => sum + computeLineTotal(item), 0);
  const operationFeeAmount = (baseAmount * operationFeePercent) / 100;
  const totalAmount = baseAmount + operationFeeAmount;
  const handleCopyReference = () => {
    if (!form.payment_reference) return;
    if (typeof navigator !== "undefined" && navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(form.payment_reference).then(() => {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: t("Reference copied"),
          showConfirmButton: false,
          timer: 2000,
        });
      }).catch(() => {
        Swal.fire({
          icon: "info",
          title: t("Copy failed"),
          text: t("Please copy the reference manually."),
          confirmButtonColor: brand,
        });
      });
    } else {
      Swal.fire({
        icon: "info",
        title: t("Copy not supported"),
        text: t("Select and copy the reference manually."),
        confirmButtonColor: brand,
      });
    }
  };

  const handleLineItemChange = (index, key, value) => {
    setLineItems((items) =>
      items.map((item, i) => (i === index ? { ...item, [key]: value } : item))
    );
  };

  const handleAddLineItem = () => {
    setLineItems((items) => [...items, createEmptyInvoiceLineItem()]);
    setLineItemErrors((errors) => [...errors, {}]);
  };

  const handleRemoveLineItem = (index) => {
    setLineItems((items) => (items.length === 1 ? items : items.filter((_, i) => i !== index)));
    setLineItemErrors((errors) => (errors.length === 1 ? errors : errors.filter((_, i) => i !== index)));
  };

  const handleSave = () => {
    const errors = {};
    const itemErrors = lineItems.map(() => ({}));
    let hasLineItemErrors = false;

    if (!form.customer_name) {
      errors.customer_name = t("Customer name is required");
    }
    if (!form.customer_phone) {
      errors.customer_phone = t("Customer phone is required");
    }
    if (!form.customer_address) {
      errors.customer_address = t("Customer address is required");
    }
    lineItems.forEach((item, index) => {
      if (!item.description.trim()) {
        itemErrors[index].description = t("Description is required");
        hasLineItemErrors = true;
      }
      const quantity = Number(item.quantity);
      if (!quantity || quantity <= 0) {
        itemErrors[index].quantity = t("Quantity must be greater than 0");
        hasLineItemErrors = true;
      }
      const unitPrice = Number(item.unit_price);
      if (!unitPrice || unitPrice <= 0) {
        itemErrors[index].unit_price = t("Unit price must be greater than 0");
        hasLineItemErrors = true;
      }
    });

    if (!baseAmount) {
      errors.base_amount = t("Add at least one valid line item.");
    }
    if (hasLineItemErrors) {
      errors.line_items = t("Please fix the highlighted line items.");
    }

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      setLineItemErrors(itemErrors);
      Swal.fire({
        icon: "warning",
        title: t("Missing fields"),
        text: t("Please fix the highlighted fields."),
        confirmButtonColor: brand,
      });
      return;
    }

    setFieldErrors({});
    setLineItemErrors(lineItems.map(() => ({})));
    onSave({
      business: business.id,
      customer_name: form.customer_name,
      customer_phone: form.customer_phone,
      customer_address: form.customer_address,
      base_amount: baseAmount,
      invoice_date: form.date,
      description: form.description,
      payment_reference: form.payment_reference,
      line_items: lineItems.map((item) => ({
        description: item.description.trim(),
        quantity: Number(item.quantity),
        unit_price: Number(item.unit_price),
      })),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {t("Create Invoice — {{name}}", { name: business?.name || "" })}
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField label={t("Customer Name")} fullWidth
              value={form.customer_name}
              onChange={(e) => setForm((f) => ({ ...f, customer_name: e.target.value }))}
              error={Boolean(fieldErrors.customer_name)}
              helperText={fieldErrors.customer_name}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PhoneInput
              label={t("Customer Phone")}
              fullWidth
              value={form.customer_phone}
              onChange={(val) => setForm((f) => ({ ...f, customer_phone: val }))}
              placeholder="7XXXXXXXX"
              error={Boolean(fieldErrors.customer_phone)}
              helperText={fieldErrors.customer_phone}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={t("Customer Address")}
              fullWidth
              value={form.customer_address}
              onChange={(e) => setForm((f) => ({ ...f, customer_address: e.target.value }))}
              placeholder="Street, City"
              error={Boolean(fieldErrors.customer_address)}
              helperText={fieldErrors.customer_address}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={t("Invoice Date")}
              fullWidth
              value={formatInvoiceDateForDisplay(form.date)}
              InputProps={{ readOnly: true }}
              inputProps={{ "aria-readonly": "true" }}
              helperText={t("System-generated from today's date")}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField label={t("Description (Service Rendered)")} fullWidth multiline minRows={3}
              value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{t("Line Items")}</Typography>
                <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={handleAddLineItem}>
                  {t("Add Item")}
                </Button>
              </Stack>
              <Stack spacing={2}>
                {lineItems.map((item, index) => {
                  const itemError = lineItemErrors[index] || {};
                  return (
                    <Grid container spacing={2} key={index} alignItems="center">
                      <Grid item xs={12} md={5}>
                        <TextField
                          label={t("Item {{index}} Description", { index: index + 1 })}
                          fullWidth
                          value={item.description}
                          onChange={(e) => handleLineItemChange(index, "description", e.target.value)}
                          error={Boolean(itemError.description)}
                          helperText={itemError.description}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4} md={2}>
                        <TextField
                          label={t("Quantity")}
                          fullWidth
                          value={item.quantity ? Number(item.quantity).toLocaleString("en-US") : ""}
                          onChange={(e) => {
                            const val = e.target.value.replace(/,/g, "");
                            if (!isNaN(val)) handleLineItemChange(index, "quantity", val);
                          }}
                          error={Boolean(itemError.quantity)}
                          helperText={itemError.quantity}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4} md={3}>
                        <TextField
                          label={t("Unit Price (TZS)")}
                          fullWidth
                          value={item.unit_price ? Number(item.unit_price).toLocaleString("en-US") : ""}
                          onChange={(e) => {
                            const val = e.target.value.replace(/,/g, "");
                            if (!isNaN(val)) handleLineItemChange(index, "unit_price", val);
                          }}
                          error={Boolean(itemError.unit_price)}
                          helperText={itemError.unit_price}
                        />
                      </Grid>
                      <Grid item xs={10} sm={3} md={2}>
                        <TextField
                          label={t("Line Total")}
                          fullWidth
                          value={`${currency(computeLineTotal(item))} TZS`}
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                      <Grid item xs={2} sm={1} md={1}>
                        <Tooltip title={t("Remove item")}>
                          <span>
                            <IconButton
                              disabled={lineItems.length === 1}
                              onClick={() => handleRemoveLineItem(index)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  );
                })}
              </Stack>
              {fieldErrors.line_items && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
                  {fieldErrors.line_items}
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={t("Service Amount (TZS)")}
              fullWidth
              value={`${currency(baseAmount)} TZS`}
              InputProps={{ readOnly: true }}
              error={Boolean(fieldErrors.base_amount)}
              helperText={fieldErrors.base_amount || t("Sum of all line item totals")}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t("Invoice Payment Reference")}
              fullWidth
              value={form.payment_reference}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={t("Copy reference")}>
                      <span>
                        <IconButton size="small" onClick={handleCopyReference}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              helperText={t("Share this reference with customers at the bank to ensure matching payments.")}
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, p: 2, borderRadius: 2, bgcolor: "#f8f8f8" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{t("Summary")}</Typography>
          <Typography variant="body2" sx={{ fontFamily: "monospace", mb: 1 }}>
            {t("Payment Reference: {{ref}}", { ref: form.payment_reference })}
          </Typography>
          <Stack spacing={0.5} sx={{ mb: 1 }}>
            {lineItems.map((item, index) => (
              <Typography variant="body2" key={`summary-item-${index}`}>
                {t("Summary line", {
                  index: index + 1,
                  description: (item.description || t("Item")).trim() || t("Item"),
                  quantity: item.quantity || 0,
                  unit: currency(item.unit_price || 0),
                  total: currency(computeLineTotal(item)),
                })}
              </Typography>
            ))}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {t("Summary totals", {
              subtotal: currency(baseAmount),
              percent: operationFeePercent,
              fee: currency(operationFeeAmount),
              total: currency(totalAmount),
            })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("Special note: SSC is retained automatically. Customer should pay the total amount shown above.")}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {t("Muhasibu approval note")}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("Cancel")}</Button>
        <Button variant="contained" sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }} onClick={handleSave}>
          {t("Send for Approval")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const BusinessDetailsDialog = ({ open, onClose, business, onCreateInvoice, currentRole, onDownloadInvoice }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  if (!business) return null;

  const totalIncome = (business.invoices || [])
    .filter((i) => i.status === INVOICE_STATUS.APPROVED)
    .reduce((sum, i) => sum + Number(i.total_amount || i.amount || 0), 0);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {t("{{name}} — Details", { name: business.name })}
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">{t("Type")}</Typography>
            <Typography fontWeight={700}>{business.business_type}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">{t("Capital")}</Typography>
            <Typography fontWeight={700}>{currency(business.capital)} TZS</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">{t("Status")}</Typography>
            <StatusChip status={business.status} />
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">{t("Total Income")}</Typography>
            <Typography fontWeight={700}>{currency(totalIncome)} TZS</Typography>
          </Grid>
        </Grid>

        <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label={t("Invoices")} />
          <Tab label={t("Audit Trail")} />
        </Tabs>

        {tab === 0 && (
          <Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              {currentRole === "Mwenyekiti" && business.status === BUSINESS_STATUS.ACTIVE && (
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  variant="contained"
                  sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
                  onClick={() => onCreateInvoice(business)}
                >
                  {t("New Invoice")}
                </Button>
              )}
            </Box>
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>{t("Date")}</TableCell>
                    <TableCell>{t("Payment Ref")}</TableCell>
                    <TableCell>{t("Customer")}</TableCell>
                    <TableCell>{t("Contact")}</TableCell>
                    <TableCell>{t("Items")}</TableCell>
                    <TableCell>{t("Subtotal")}</TableCell>
                    <TableCell>{t("SSC")}</TableCell>
                    <TableCell>{t("Amount To Pay")}</TableCell>
                    <TableCell>{t("Paid")}</TableCell>
                    <TableCell>{t("Payment Status")}</TableCell>
                    <TableCell>{t("Status")}</TableCell>
                    <TableCell>{t("Chain")}</TableCell>
                    <TableCell align="right">{t("Actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(business.invoices || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={14} align="center" sx={{ py: 4, color: "text.secondary" }}>
                        {t("No invoices yet.")}
                      </TableCell>
                    </TableRow>
                  ) : (business.invoices || []).map((inv, i) => {
                    const paymentMeta = getInvoicePaymentMeta(inv, t);
                    const lineItems = Array.isArray(inv.line_items) ? inv.line_items : [];
                    const itemCount = lineItems.length;
                    return (
                      <TableRow key={inv.id} hover>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell>{inv.created_at ? formatDate(inv.created_at) : "—"}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">{inv.payment_reference || "—"}</Typography>
                        </TableCell>
                        <TableCell>{inv.customer_name}</TableCell>
                        <TableCell>
                          <Stack spacing={0}>
                            <Typography variant="body2">{inv.customer_phone || "—"}</Typography>
                            <Typography variant="caption" color="text.secondary">{inv.customer_address || "—"}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={formatLineItemsTooltip(t, lineItems)}>
                            <Chip label={t("Item count label", { count: itemCount })} size="small" variant="outlined" />
                          </Tooltip>
                        </TableCell>
                        <TableCell>{currency(inv.base_amount)} TZS</TableCell>
                        <TableCell>{currency(inv.operation_fee_amount)} TZS</TableCell>
                        <TableCell>{currency(paymentMeta.payable)} TZS</TableCell>
                        <TableCell>
                          {paymentMeta.showPayment ? `${currency(paymentMeta.paid)} TZS` : (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" color={paymentMeta.statusColor} label={paymentMeta.statusLabel} />
                        </TableCell>
                        <TableCell><InvoiceStatusChip status={inv.status} /></TableCell>
                        <TableCell><ApprovalChain status={inv.status} /></TableCell>
                        <TableCell align="right">
                          {inv.can_download ? (
                            <Tooltip title={t("Download Invoice PDF")}>
                              <IconButton size="small" onClick={() => onDownloadInvoice(inv)}>
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.secondary">{t("Pending approvals")}</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {tab === 1 && (
          <Box>
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>{t("Action")}</TableCell>
                    <TableCell>{t("By")}</TableCell>
                    <TableCell>{t("At")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {business?.audit?.map((a, i) => (
                    <TableRow key={i}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{a.action}</TableCell>
                      <TableCell>{a.by}</TableCell>
                      <TableCell>{a.at}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("Close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

// ======= Main Page =======
export default function GroupBusinesses() {
  const { t } = useTranslation();
  const { role: currentRole, loading: authLoading } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [openRegister, setOpenRegister] = useState(false); // State for opening/closing the register business dialog
  const [openDetails, setOpenDetails] = useState(false);
  const [detailsBiz, setDetailsBiz] = useState(null);
  const [openInvoice, setOpenInvoice] = useState(false);
  const [allInvoices, setAllInvoices] = useState([]); // New state for all invoices
  const [businessNameMap, setBusinessNameMap] = useState(new Map()); // New state for business name lookup
  const [invoiceBiz, setInvoiceBiz] = useState(null);
  const [mainTab, setMainTab] = useState(0);
  const [invoiceFeedback, setInvoiceFeedback] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const summary = useMemo(() => {
    const active = businesses.filter(b => b.status === BUSINESS_STATUS.ACTIVE).length;
    const pendingAdminApproval = businesses.filter(b => b?.status === BUSINESS_STATUS.PENDING_APPROVAL).length;

    // Calculate pending invoices for Katibu and Muhasibu
    const pendingInvoices = businesses.reduce((acc, b) => {
      return acc + (b.invoices || []).filter(i =>
        [INVOICE_STATUS.PENDING_SECRETARY_APPROVAL, INVOICE_STATUS.PENDING_ACCOUNTANT_APPROVAL].includes(i.status)
      ).length;
    }, 0);

    const totalIncome = businesses.reduce((acc, b) => {
      const inc = (b.invoices || [])
        .filter(i => i.status === INVOICE_STATUS.APPROVED)
        .reduce((s, i) => s + Number(i.total_amount || i.amount || 0), 0);
      return acc + inc;
    }, 0);

    return { active, pendingAdminApproval, pendingInvoices, totalIncome };
  }, [businesses]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  const loadBusinesses = useCallback(async ({ showLoading = true } = {}) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);
      const [businessesData, invoicesData] = await Promise.all([
        fetchGroupBusinesses(),
        fetchBusinessInvoices(),
      ]);

      // Create a map for quick business name lookup
      const nameMap = new Map(businessesData.map(b => [b.id, b.name]));
      setBusinessNameMap(nameMap);
      setAllInvoices(invoicesData); // Store all invoices

      // Map invoices to their respective businesses
      const businessesWithInvoices = businessesData.map(business => ({
        ...business,
        invoices: invoicesData.filter(invoice => invoice.business === business.id),
      }));

      setBusinesses(businessesWithInvoices);
      setDetailsBiz((current) => {
        if (!current) return current;
        return businessesWithInvoices.find((business) => business.id === current.id) || current;
      });
    } catch (err) {
      setError(err.message);
      Swal.fire({ icon: "error", title: t("Failed to load data"), text: err.message, confirmButtonColor: brand });
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [t]);

  useEffect(() => {
    if (!authLoading) {
      loadBusinesses();
    }
  }, [authLoading, loadBusinesses]);


  // ===== Role-aware actions =====
  const canRegister = currentRole === "Mwenyekiti";
  const canAdminApprove = (biz) => currentRole === "Admin" && biz.status === BUSINESS_STATUS.PENDING_APPROVAL;

  const canCreateInvoice = (biz) =>
    currentRole === "Mwenyekiti" && biz.status === BUSINESS_STATUS.ACTIVE;

  const canKatibuApproveInvoice = (inv) => currentRole === "Katibu" && inv.status === INVOICE_STATUS.PENDING_SECRETARY_APPROVAL;
  const canMuhasibuApproveInvoice = (inv) => currentRole === "Muhasibu" && inv.status === INVOICE_STATUS.PENDING_ACCOUNTANT_APPROVAL;

  // ===== Handlers =====
  const handleOpenDetails = (biz) => {
    setDetailsBiz(biz);
    setOpenDetails(true);
  };

  const handleRegisterSave = async (payload) => {
    try {
      await createGroupBusiness(payload);
      setOpenRegister(false);
      Swal.fire({ icon: "success", title: t("Submitted for Admin approval"), confirmButtonColor: brand });
      loadBusinesses(); // Refresh the list
    } catch (err) {
      Swal.fire({ icon: "error", title: t("Registration Failed"), text: err.message, confirmButtonColor: brand });
    }
  };

  const handleCreateInvoiceClick = (biz) => {
    setInvoiceBiz(biz);
    setOpenInvoice(true);
  };

  const handleCreateInvoiceSave = async (invoice) => {
    try {
      await createBusinessInvoice(invoice);
      setOpenInvoice(false);
      await loadBusinesses({ showLoading: false });
      setInvoiceFeedback({
        open: true,
        severity: "success",
        message: t("Invoice created and sent for approval successfully."),
      });
    } catch (err) {
      setInvoiceFeedback({
        open: true,
        severity: "error",
        message: err.message || t("Invoice creation failed. Please try again."),
      });
    }
  };

  const handleCloseInvoiceFeedback = (_event, reason) => {
    if (reason === "clickaway") return;
    setInvoiceFeedback((current) => ({ ...current, open: false }));
  };

  const handleAdminApproveBusiness = async (biz) => {
    const result = await Swal.fire({
      title: t("Approve Business?"),
      text: t('Activate "{{name}}"', { name: biz.name }),
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: brand,
    });

    if (result.isConfirmed) {
      try {
        await approveGroupBusiness(biz.id);
        Swal.fire({ icon: "success", title: t("Business approved"), confirmButtonColor: brand });
        loadBusinesses(); // Refresh the list
      } catch (err) {
        Swal.fire({ icon: "error", title: t("Approval Failed"), text: err.message, confirmButtonColor: brand });
      }
    }
  };

  const handleApproveInvoice = async (biz, inv) => {
    if (canKatibuApproveInvoice(inv)) {
      try {
        await approveBusinessInvoice(biz.id, inv.id, "approve", "Katibu approved");
        Swal.fire({ icon: "success", title: t("Approved → Muhasibu"), confirmButtonColor: brand });
        loadBusinesses(); // Refresh the list
      } catch (err) {
        Swal.fire({ icon: "error", title: t("Approval Failed"), text: err.message, confirmButtonColor: brand });
      }
    } else if (canMuhasibuApproveInvoice(inv)) {
      try {
        await approveBusinessInvoice(biz.id, inv.id, "approve", "Muhasibu approved");
        Swal.fire({ icon: "success", title: t("Approved (awaiting bank payment capture)"), confirmButtonColor: brand });
        loadBusinesses(); // Refresh the list
      } catch (err) {
        Swal.fire({ icon: "error", title: t("Approval Failed"), text: err.message, confirmButtonColor: brand });
      }
    }
  };

  const generateInvoicePDF = (payload) => {
    const doc = new jsPDF();
    const issuedDate = payload.created_at ? dayjs(payload.created_at).format("DD MMM YYYY HH:mm") : dayjs().format("DD MMM YYYY HH:mm");
    const pageWidth = doc.internal.pageSize.getWidth();
    const leftColumnX = 14;
    const rightColumnX = pageWidth - 14;
    const lineHeight = 6;
    const totalAmountValue = Number(payload.total_amount || payload.amount || 0);
    const subtotalAmountValue = Number(payload.base_amount || 0);
    const ofAmountValue = Number(payload.operation_fee_amount || 0);
    const subtotalAmount = currency(subtotalAmountValue);
    const ofAmount = currency(ofAmountValue);
    const totalAmount = currency(totalAmountValue);
    const lineItems = Array.isArray(payload.line_items) ? payload.line_items : [];

    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text(`${payload.group_name || "Group"} - Business Invoice`, 14, 20);

    const brandRGB = { r: 128, g: 11, b: 51 };

    doc.setFontSize(11);
    doc.text(`Invoice #: ${payload.id}`, leftColumnX, 30);
    doc.text(`Issued: ${issuedDate}`, leftColumnX, 30 + lineHeight);
    doc.text(`Business: ${payload.business_name || "N/A"}`, leftColumnX, 30 + lineHeight * 2);
    doc.text(`Group NMB Account: ${payload.group_account_number || "N/A"}`, leftColumnX, 30 + lineHeight * 3);
    doc.text(`Payment Reference: ${payload.payment_reference || payload.id}`, leftColumnX, 30 + lineHeight * 4);

    doc.text(`Customer: ${payload.customer_name}`, rightColumnX, 30, { align: "right" });
    doc.text(`Phone: ${payload.customer_phone}`, rightColumnX, 30 + lineHeight, { align: "right" });
    doc.text(`Address: ${payload.customer_address}`, rightColumnX, 30 + lineHeight * 2, { align: "right" });

    const description = payload.description || "N/A";
    const descriptionStartY = 30 + lineHeight * 6;
    doc.text("Service Description:", leftColumnX, descriptionStartY);
    doc.text(doc.splitTextToSize(description, 180), leftColumnX, descriptionStartY + lineHeight);

    let nextY = 102;
    if (lineItems.length) {
      autoTable(doc, {
        startY: nextY,
        head: [["#", "Item", "Qty", "Unit Price (TZS)", "Line Total (TZS)"]],
        body: lineItems.map((item, idx) => {
          const qty = Number(item.quantity) || 0;
          const unit = Number(item.unit_price) || 0;
          const lineTotal = qty * unit;
          return [
            idx + 1,
            (item.description || "Item").trim() || "Item",
            qty,
            currency(unit),
            currency(lineTotal),
          ];
        }),
        columnStyles: {
          0: { halign: "right" },
          2: { halign: "right" },
          3: { halign: "right" },
          4: { halign: "right" },
        },
        headStyles: {
          fillColor: [brandRGB.r, brandRGB.g, brandRGB.b],
          textColor: [255, 255, 255],
        },
        alternateRowStyles: {
          fillColor: [249, 241, 244],
        },
      });
      nextY = doc.lastAutoTable.finalY + 8;
    }

    autoTable(doc, {
      startY: nextY,
      head: [["Item", "Amount (TZS)"]],
      body: [
        ["Subtotal", subtotalAmount],
        [`System Service Charge (${payload.operation_fee_percent}%)`, ofAmount],
        ["Total Payable", `${totalAmount}`],
      ],
      styles: { halign: "right" },
      columnStyles: {
        0: { halign: "left" },
      },
      headStyles: {
        fillColor: [brandRGB.r, brandRGB.g, brandRGB.b],
        textColor: [255, 255, 255],
      },
      alternateRowStyles: {
        fillColor: [249, 241, 244],
      },
    });

    doc.save(`GroupInvoice-${payload.id}.pdf`);
  };

  const handleDownloadInvoice = async (invoice) => {
    try {
      const payload = await downloadBusinessInvoice(invoice.id);
      generateInvoicePDF(payload);
    } catch (err) {
      Swal.fire({ icon: "error", title: t("Download Failed"), text: err.message, confirmButtonColor: brand });
    }
  };

  if (authLoading || loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Typography color="error" align="center" sx={{ my: 4 }}>
        {t("Error: {{message}}", { message: error })}
      </Typography>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 900, color: brand }}>
          {t("Group Businesses")}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary">
          {t("Register, approve, and manage group-owned business income.")}
        </Typography>
      </Box>

      {/* Summary */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<StorefrontIcon sx={{ fontSize: 26, color: brand }} />}
            label={t("Active Businesses")}
            value={summary.active}
            index={0}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<PendingActionsIcon sx={{ fontSize: 26, color: brand }} />}
            label={t("Pending Admin Approval")}
            value={summary.pendingAdminApproval}
            index={0.5}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<PendingActionsIcon sx={{ fontSize: 26, color: brand }} />}
            label={t("Pending Invoices")}
            value={summary.pendingInvoices}
            index={1}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<MonetizationOnIcon sx={{ fontSize: 26, color: brand }} />}
            label={t("Total Business Income")}
            value={`${currency(summary.totalIncome)} TZS`}
            index={2}
          />
        </Grid>
      </Grid>

      {/* Toolbar for primary actions */}
      <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 3 }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {t("Business Overview")}
          </Typography>
          {canRegister && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenRegister(true)}
              sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
            >
              {t("Register New Business")}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Main Content Card with Tabs */}
      <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
        <CardContent sx={{ p: 2 }}>
          <Tabs value={mainTab} onChange={(_e, v) => setMainTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Tab label={t("Business List")} />
            <Tab label={t("Invoices For My Action")} />
          </Tabs>
          {/* Tab 1: Business List */}
          {mainTab === 0 && (
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Business")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Type")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Capital")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Status")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Created")}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t("Actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {businesses.map((b, idx) => (
                    <TableRow key={b.id} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{b.name}</TableCell>
                      <TableCell>{b.business_type}</TableCell>
                      <TableCell>{currency(b.capital)} TZS</TableCell>
                      <TableCell><StatusChip status={b.status} /></TableCell>
                      <TableCell>{formatDate(b.created_at)}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title={t("View Details")}>
                            <IconButton size="small" aria-label={t("View Details")} onClick={() => handleOpenDetails(b)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {canAdminApprove(b) && (
                            <Tooltip title={t("Approve Business")}>
                              <IconButton size="small" color="success" onClick={() => handleAdminApproveBusiness(b)}>
                                <TaskAltIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {businesses.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 5, color: "text.secondary" }}>
                        {t("No businesses registered yet.")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Tab 2: Invoices for My Action */}
          {mainTab === 1 && (
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Business")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Date")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Customer")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Items")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Payment Ref")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Amount To Pay")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Paid")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Payment Status")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Status")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Chain")}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t("My Action")}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t("Download")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allInvoices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={12} align="center" sx={{ py: 5, color: "text.secondary" }}>
                        {t("No invoices found.")}
                      </TableCell>
                    </TableRow>
                  ) : (
                    allInvoices.map((inv) => {
                      const paymentMeta = getInvoicePaymentMeta(inv, t);
                      const lineItems = Array.isArray(inv.line_items) ? inv.line_items : [];
                      const itemCount = lineItems.length;
                      return (
                        <TableRow key={inv.id} hover>
                          <TableCell>{inv.business_name || businessNameMap.get(inv.business) || t("Unknown Business")}</TableCell>
                          <TableCell>{inv.created_at ? formatDate(inv.created_at) : "—"}</TableCell>
                          <TableCell>{inv.customer_name}</TableCell>
                          <TableCell>
                            <Tooltip title={formatLineItemsTooltip(t, lineItems)}>
                              <Chip size="small" variant="outlined" label={t("Item count label", { count: itemCount })} />
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">{inv.payment_reference || "—"}</Typography>
                          </TableCell>
                          <TableCell>{currency(paymentMeta.payable)} TZS</TableCell>
                          <TableCell>
                            {paymentMeta.showPayment ? `${currency(paymentMeta.paid)} TZS` : (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip size="small" color={paymentMeta.statusColor} label={paymentMeta.statusLabel} />
                          </TableCell>
                          <TableCell><InvoiceStatusChip status={inv.status} /></TableCell>
                          <TableCell><ApprovalChain status={inv.status} /></TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              {(canKatibuApproveInvoice(inv) || canMuhasibuApproveInvoice(inv)) && ( // Pass business ID to handler
                                <Button size="small" variant="contained" startIcon={<CheckCircleIcon />} onClick={() => handleApproveInvoice({ id: inv.business }, inv)} sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}>
                                  {t("Approve")}
                                </Button>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            {inv.can_download ? (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<DownloadIcon fontSize="small" />}
                                onClick={() => handleDownloadInvoice(inv)}
                              >
                                {t("PDF")}
                              </Button>
                            ) : (
                              <Typography variant="caption" color="text.secondary">{t("Pending")}</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

        </CardContent>
      </Card>

      {/* Dialogs */}
      <RegisterBusinessDialog
        open={openRegister}
        onClose={() => setOpenRegister(false)}
        onSave={handleRegisterSave}
      />

      <BusinessDetailsDialog
        open={openDetails}
        onClose={() => setOpenDetails(false)}
        business={detailsBiz}
        currentRole={currentRole}
        onCreateInvoice={handleCreateInvoiceClick}
        onDownloadInvoice={handleDownloadInvoice}
      />

      <CreateInvoiceDialog
        open={openInvoice}
        onClose={() => setOpenInvoice(false)}
        onSave={handleCreateInvoiceSave}
        business={invoiceBiz}
      />
      <Snackbar
        open={invoiceFeedback.open}
        autoHideDuration={5000}
        onClose={handleCloseInvoiceFeedback}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 10 }}
      >
        <Alert
          onClose={handleCloseInvoiceFeedback}
          severity={invoiceFeedback.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {invoiceFeedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
